// src/prototype/components/EATabBar.tsx
// ============================================================
// Two-mode tab bar for the Evaluate page
// Tabs: Portfolio Strategy | Use Case Explorer
// Export / Share buttons live on the same row, right-aligned
// Sits in EAJourneyBarFrame's tab slot: the frame supplies the gap ABOVE the row and
// nothing below it, so the row stays flush against the content section it controls.
// This component must not add its own vertical margin.
// Microcopy replaced with tooltip icons (Phase 1 / Item 4)
// UX update March 2026: renamed tabs + artifact-level actions
// UX update March 2026: Adjust Weights added as contextual utility
//   action — only visible on Use Case Explorer when weights have
//   been customized. Sits left of Export/Share with a separator.
// ============================================================

import React, { useState } from 'react';
import { FileBarChart2, Search, Info, FileDown, Share2, SlidersHorizontal, Grid2x2 } from 'lucide-react';

export type EvaluateTab = 'report' | 'explore' | 'matrix';

interface TabConfig {
  id: EvaluateTab;
  label: string;
  icon: React.ReactNode;
  tooltip: string;
}

const TABS: TabConfig[] = [
  {
    id: 'report',
    label: 'Portfolio Strategy',
    icon: <FileBarChart2 className="w-3.5 h-3.5" />,
    tooltip: 'Executive portfolio summary, strategic findings, and recommendations.',
  },
  {
    id: 'explore',
    label: 'Use Case Explorer',
    icon: <Search className="w-3.5 h-3.5" />,
    tooltip: 'Filter, compare ROI, flag assumptions, and select initiatives.',
  },
  {
    id: 'matrix',
    label: 'Priority Matrix',
    icon: <Grid2x2 className="w-3.5 h-3.5" />,
    tooltip: 'Financial value by ease of implementation, with the prioritized list by VRI score.',
  },
];

// ── Inline Tooltip ─────────────────────────────────────────────────────────
const TabTooltip: React.FC<{ text: string; isActive: boolean }> = ({ text, isActive }) => {
  const [visible, setVisible] = useState(false);

  return (
    <span
      className="relative flex items-center"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <Info
        className={`w-3 h-3 transition-colors ${
          isActive ? 'text-white/60 hover:text-white' : 'text-[#18365E]/30 hover:text-[#18365E]/60'
        }`}
      />
      {visible && (
        <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-44 px-2.5 py-1.5 bg-[#18365E] text-white text-[10px] leading-snug rounded-lg shadow-lg pointer-events-none z-50 whitespace-normal">
          {text}
          {/* Arrow */}
          <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-[#18365E]" />
        </span>
      )}
    </span>
  );
};

// ── Tab Bar ────────────────────────────────────────────────────────────────
interface EATabBarProps {
  activeTab: EvaluateTab;
  onChange: (tab: EvaluateTab) => void;
  /** Show "Adjust Weights" utility button — only when on explore tab + weights customized */
  showAdjustWeights?: boolean;
  /** Handler for Adjust Weights click */
  onAdjustWeights?: () => void;
}

export const EATabBar: React.FC<EATabBarProps> = ({
  activeTab,
  onChange,
  showAdjustWeights = false,
  onAdjustWeights,
}) => {
  const handleExport = () => window.open('/report/print', '_blank');

  const handleShare = async () => {
    try {
      await navigator.clipboard?.writeText(window.location.href);
    } catch {
      // no-op — clipboard may not be available
    }
  };

  return (
    <div className="flex items-center justify-between gap-3">

      {/* Left: pill tabs */}
      <div className="flex items-center gap-1 p-1 bg-[#18365E]/[0.06] rounded-xl w-fit">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                isActive
                  ? 'bg-[#18365E] text-white shadow-sm'
                  : 'text-[#18365E]/70 hover:text-[#18365E] hover:bg-white/60'
              }`}
            >
              <span className={isActive ? 'text-white' : 'text-[#18365E]/50'}>
                {tab.icon}
              </span>
              {tab.label}
              <TabTooltip text={tab.tooltip} isActive={isActive} />
            </button>
          );
        })}
      </div>

      {/* Right: artifact-level actions */}
      <div className="flex items-center gap-2 shrink-0">

        {/* Adjust Weights — contextual, only on explore tab when customized */}
        {showAdjustWeights && onAdjustWeights && (
          <>
            <button
              onClick={onAdjustWeights}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e5e7eb] rounded-lg text-[12px] font-semibold text-[#18365E] bg-white hover:bg-[#F5F7FA] transition-colors"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Adjust Weights
            </button>
            <div className="w-px h-4 bg-[#e5e7eb]" />
          </>
        )}

        <button
          onClick={handleExport}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e5e7eb] rounded-lg text-[12px] font-semibold text-[#18365E] bg-white hover:bg-[#F5F7FA] transition-colors"
        >
          <FileDown className="w-3.5 h-3.5" />
          Export
        </button>
        <button
          onClick={handleShare}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e5e7eb] rounded-lg text-[12px] font-semibold text-[#18365E] bg-white hover:bg-[#F5F7FA] transition-colors"
        >
          <Share2 className="w-3.5 h-3.5" />
          Share
        </button>
      </div>

    </div>
  );
};

export default EATabBar;